import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot} from '@angular/router';
import {
  IpopExplicitSaveConfigurationComponent
} from './components/views/settings/ipop-explicit-save/ipop-explicit-save-configuration.component';

const ipopExplicitSaveRoutePath = 'settings/ipop-explicit-save/:accountId';
const unsavedRuleNavigationConfirmationMessage = 'You have unsaved rule changes. Leave this page and discard them?';

@Injectable({
  providedIn: 'root'
})
export class IpopExplicitSaveDirtyNavigationGuard implements CanDeactivate<IpopExplicitSaveConfigurationComponent> {
  /**
   * Confirms navigation away when the explicit-save rule change set is dirty.
   */
  canDeactivate(
    component: IpopExplicitSaveConfigurationComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): boolean
  {
    let canDeactivate = true;
    const isNavigationAway = !this.isSamePathNavigation(currentRoute, currentState, nextState);

    if (component.hasUnsavedRuleChanges() && isNavigationAway) {
      canDeactivate = window.confirm(unsavedRuleNavigationConfirmationMessage);
    }

    if (canDeactivate && component.siteWideUnlockForm?.dirty && isNavigationAway) {
      canDeactivate = window.confirm('You have unsaved Sitewide Unlock settings. Leave this page and discard them?');
    }

    return canDeactivate;
  }

  /**
   * Identifies query-only navigation that keeps the current explicit-save route active.
   */
  private isSamePathNavigation(
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): boolean
  {
    let isSamePath = false;
    const isExplicitSaveRoute = currentRoute.routeConfig?.path === ipopExplicitSaveRoutePath;

    if (isExplicitSaveRoute && nextState) {
      const currentPath = this.getUrlPath(currentState.url);
      const nextPath = this.getUrlPath(nextState.url);
      isSamePath = currentPath === nextPath;
    }

    return isSamePath;
  }

  /**
   * Removes query and fragment values before comparing route paths.
   */
  private getUrlPath(url: string): string
  {
    const queryIndex = url.indexOf('?');
    const fragmentIndex = url.indexOf('#');
    let pathEndIndex = url.length;

    if (queryIndex >= 0 && queryIndex < pathEndIndex) {
      pathEndIndex = queryIndex;
    }
    if (fragmentIndex >= 0 && fragmentIndex < pathEndIndex) {
      pathEndIndex = fragmentIndex;
    }
    const path = url.substring(0, pathEndIndex);

    return path;
  }
}
